import { useState, useEffect } from "react";
import { DollarSign, TrendingUp, Receipt, CreditCard, Download, Calendar, Users, ArrowUp, ArrowDown } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from "recharts";
import { toast } from "sonner";
import { getRevenue } from "@/services/api";

export const Revenue = () => {
  const [timeRange, setTimeRange] = useState("6months");
  const [monthlyData, setMonthlyData] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    subscriptionRevenue: 0,
    oneTimeRevenue: 0,
    growth: 0,
    avgPerUser: 0,
    totalUsers: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const revenueData = await getRevenue(timeRange);

        if (revenueData && revenueData.success) {
          const { stats: revenueStats, invoices } = revenueData;
          
          setStats({
            totalRevenue: revenueStats.totalRevenue || 0,
            subscriptionRevenue: revenueStats.subscriptionRevenue || 0,
            oneTimeRevenue: revenueStats.oneTimeRevenue || 0,
            growth: revenueStats.growth || 0,
            avgPerUser: revenueStats.avgPerUser || 0,
            totalUsers: revenueStats.totalUsers || 0
          });

          setMonthlyData(revenueStats.monthlyRevenue || []);
          setRecentInvoices(invoices || []);
        }
      } catch (error) {
        console.error("Error fetching revenue data:", error);
        toast.error("Failed to load revenue data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange]);

  const handleExportInvoices = () => {
    toast.success("Invoices exported successfully");
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Revenue Management</h1>
          <p className="text-muted-foreground mt-1">Track subscriptions, payments, and generate invoices</p>
        </div>
        <Button
          onClick={handleExportInvoices}
          className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-md"
        >
          <Download className="w-4 h-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Revenue</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">
                  ₹{stats.totalRevenue.toLocaleString('en-IN')}
                </h3>
                <div className="flex items-center gap-1 mt-2">
                  {stats.growth >= 0 ? (
                    <>
                      <ArrowUp className="w-4 h-4 text-success" />
                      <span className="text-sm font-medium text-success">+{stats.growth}%</span>
                    </>
                  ) : (
                    <>
                      <ArrowDown className="w-4 h-4 text-destructive" />
                      <span className="text-sm font-medium text-destructive">{stats.growth}%</span>
                    </>
                  )}
                  <span className="text-xs text-muted-foreground ml-1">vs last month</span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center shadow-md">
                <DollarSign className="w-6 h-6 text-primary-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Subscriptions</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">
                  ₹{stats.subscriptionRevenue.toLocaleString('en-IN')}
                </h3>
                <p className="text-xs text-muted-foreground mt-2">
                  {stats.totalRevenue > 0 
                    ? `${Math.round((stats.subscriptionRevenue / stats.totalRevenue) * 100)}% of total`
                    : 'No revenue yet'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center shadow-md">
                <CreditCard className="w-6 h-6 text-primary-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">One-time Pickups</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">
                  ₹{stats.oneTimeRevenue.toLocaleString('en-IN')}
                </h3>
                <p className="text-xs text-muted-foreground mt-2">
                  {stats.totalRevenue > 0 
                    ? `${Math.round((stats.oneTimeRevenue / stats.totalRevenue) * 100)}% of total`
                    : 'No revenue yet'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center shadow-md">
                <Receipt className="w-6 h-6 text-primary-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg. Per User</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">
                  ₹{stats.avgPerUser.toLocaleString('en-IN')}
                </h3>
                <div className="flex items-center gap-1 mt-2">
                  <Users className="w-3 h-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{stats.totalUsers} active users</span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary-dark flex items-center justify-center shadow-md">
                <DollarSign className="w-6 h-6 text-primary-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Chart */}
      <Card className="border-border">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Revenue Trends
              </CardTitle>
              <CardDescription>Breakdown of subscription and one-time revenues</CardDescription>
            </div>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3months">Last 3 months</SelectItem>
                <SelectItem value="6months">Last 6 months</SelectItem>
                <SelectItem value="1year">Last year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
              <YAxis stroke="hsl(var(--muted-foreground))" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                }}
                formatter={(value) => `₹${value.toLocaleString('en-IN')}`}
              />
              <Legend />
              <Bar dataKey="subscriptions" fill="hsl(var(--primary))" name="Subscription Revenue" radius={[4, 4, 0, 0]} />
              <Bar dataKey="oneTime" fill="hsl(var(--secondary))" name="One-time Revenue" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Recent Invoices */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-primary" />
            Recent Invoices
          </CardTitle>
          <CardDescription>Latest payment transactions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice ID</TableHead>
                  <TableHead className="hidden md:table-cell">User</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead className="hidden lg:table-cell">Date</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentInvoices.map((invoice) => (
                  <TableRow key={invoice.id} className="hover:bg-muted/50 transition-smooth">
                    <TableCell className="font-medium">{invoice.id}</TableCell>
                    <TableCell className="hidden md:table-cell">{invoice.user}</TableCell>
                    <TableCell>
                      <Badge variant={invoice.type === "Subscription" ? "default" : "secondary"}>
                        {invoice.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-semibold">
                      ₹{invoice.amount}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {new Date(invoice.date).toLocaleDateString('en-IN')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={invoice.status === "paid" ? "success" : "warning"}
                        className="capitalize"
                      >
                        {invoice.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Revenue;
